import type {
  BaselineRecord,
  BaselineRecordSource,
  EstimatedValue,
  FieldMetadata,
} from "../baselines";
import { getOrgContextDefinitions } from "../baselines";
import type { OrgType, ProfileData, Tier1Inputs } from "../profile";
import {
  finiteNonNegativeEncounterVolume,
  sumTotalEncounterVolumes,
  WORKFORCE_ROLE_DEFS,
} from "../profile";
import {
  ARCHETYPES,
  archetypeEncounterDistribution,
  archetypeLeafEncounterDistribution,
  deriveProfileData,
} from "./archetypes";
import {
  computeAmbientWeightedPhysicianCost,
  computeCMIDurability,
  computeDenialRevenueIndex,
  computeMarginalPreventionFactor,
  computeNetDiscoveryIndex,
  computeNoshowRevenueIndex,
  computeOrgDenialRate,
  computeSettingAdjustment,
  computeSpecialtyWeightedNoshowValue,
  computeSurgicalRevenueIndex,
  computeWeightedContributionMargin,
  computeWeightedInpatientCodingTime,
} from "./payer-specialty-adjustments";
import {
  AVG_REVENUE_PER_ENCOUNTER_BY_PAYER,
  BASELINE_BENCHMARKS,
  CALL_RESOLUTION_RATE,
  CODING_TIME_MINUTES,
  CURRENT_CMI,
  NOSHOW_RATE_BY_PAYER,
  OR_UTILIZATION_RATE,
  ORG_BENCHMARKS,
  PA_DENIAL_RATE_BY_PAYER,
  PA_RATE_PER_ENCOUNTER,
  QA_REVIEW_RATE,
  SETTING_TYPE_ADJUSTMENTS,
  SPECIALTY_BENCHMARKS,
  SPECIALTY_DEFINITIONS,
  STATE_PAYER_MIX_ADJUSTMENTS,
  STATE_WAGE_INDEX,
  SURGICAL_ENCOUNTER_SHARE,
  TEACHING_ADJUSTMENTS,
  WORKFORCE_RATIOS,
} from "./reference-data";

// ── Revenue-to-Encounter Conversion ─────────────────────────

/**
 * Convert revenue-weighted payer shares to encounter-weighted shares.
 *
 * Users specify payer mix as "% of revenue." Downstream calculations
 * (PA volume, denial rates, no-show rates, claim value) need encounter-weighted
 * shares. The conversion uses benchmark revenue-per-encounter by payer:
 *
 *   encounter_share_i = (revenue_share_i / rev_per_encounter_i)
 *                       / Σ_j (revenue_share_j / rev_per_encounter_j)
 *
 * A payer with a high revenue share but high per-encounter revenue will get a
 * proportionally *smaller* encounter share, and vice versa.
 *
 * @param revenueShares  Payer shares expressed as fraction of total revenue (sums to ~1.0)
 * @param fallbackRevPerEncounter  Default revenue-per-encounter when a payer key
 *   has no entry in AVG_REVENUE_PER_ENCOUNTER_BY_PAYER (must be > 0).
 * @returns Encounter-weighted shares normalized to sum to 1.0
 */
export function revenueToEncounterShares(
  revenueShares: Record<string, number>,
  fallbackRevPerEncounter = 2500,
): Record<string, number> {
  const impliedVolumes: Record<string, number> = {};
  let totalImpliedVolume = 0;

  for (const [payer, revShare] of Object.entries(revenueShares)) {
    const revPerEnc =
      AVG_REVENUE_PER_ENCOUNTER_BY_PAYER[payer] ?? fallbackRevPerEncounter;
    // Implied relative encounter volume = revenue share / revenue per encounter
    const vol = revPerEnc > 0 ? revShare / revPerEnc : 0;
    impliedVolumes[payer] = vol;
    totalImpliedVolume += vol;
  }

  // Normalize to sum to 1.0
  const encounterShares: Record<string, number> = {};
  if (totalImpliedVolume > 0) {
    for (const [payer, vol] of Object.entries(impliedVolumes)) {
      encounterShares[payer] = vol / totalImpliedVolume;
    }
  } else {
    // Edge case: all zero — distribute equally
    const n = Object.keys(revenueShares).length;
    for (const payer of Object.keys(revenueShares)) {
      encounterShares[payer] = n > 0 ? 1 / n : 0;
    }
  }

  return encounterShares;
}

/**
 * Convert encounter-weighted payer shares to revenue-weighted shares.
 *
 * This is the inverse of {@link revenueToEncounterShares}. Archetype defaults
 * are stored as encounter shares; when a caller supplies partial revenue shares
 * we need to bring the defaults into revenue space before merging so that the
 * subsequent conversion back to encounter space doesn't double-convert them.
 *
 *   revenue_share_i = (encounter_share_i × rev_per_encounter_i)
 *                     / Σ_j (encounter_share_j × rev_per_encounter_j)
 *
 * @param encounterShares  Payer shares expressed as fraction of total encounters (sums to ~1.0)
 * @param fallbackRevPerEncounter  Default revenue-per-encounter when a payer key
 *   has no entry in AVG_REVENUE_PER_ENCOUNTER_BY_PAYER (must be > 0).
 * @returns Revenue-weighted shares normalized to sum to 1.0
 */
export function encounterToRevenueShares(
  encounterShares: Record<string, number>,
  fallbackRevPerEncounter = 2500,
): Record<string, number> {
  const impliedRevenue: Record<string, number> = {};
  let totalImpliedRevenue = 0;

  for (const [payer, encShare] of Object.entries(encounterShares)) {
    const revPerEnc =
      AVG_REVENUE_PER_ENCOUNTER_BY_PAYER[payer] ?? fallbackRevPerEncounter;
    const rev = encShare * revPerEnc;
    impliedRevenue[payer] = rev;
    totalImpliedRevenue += rev;
  }

  // Normalize to sum to 1.0
  const revenueShares: Record<string, number> = {};
  if (totalImpliedRevenue > 0) {
    for (const [payer, rev] of Object.entries(impliedRevenue)) {
      revenueShares[payer] = rev / totalImpliedRevenue;
    }
  } else {
    // Edge case: all zero — distribute equally
    const n = Object.keys(encounterShares).length;
    for (const payer of Object.keys(encounterShares)) {
      revenueShares[payer] = n > 0 ? 1 / n : 0;
    }
  }

  return revenueShares;
}

import type { EstimatedProfile } from "./types";

// ── Internal Types ─────────────────────────────────────────

type EstimatedBaselineValue = {
  value: number;
  source: BaselineRecordSource;
  sourceLabel: string;
};

// ── Core Entry Point ────────────────────────────────────────

/**
 * Wraps deriveProfileData() with an estimation layer.
 * Produces a full ProfileData from 5-8 Tier 1 inputs,
 * plus BaselineRecord[] tracking every baseline's source/confidence.
 */
export function estimateProfile(tier1: Tier1Inputs): EstimatedProfile {
  const orgType = tier1.org_type;
  const derivations: string[] = [];

  // 1. Estimate total encounters
  const encounterEstimate = estimateTotalEncounters(tier1);
  const totalEncounters = encounterEstimate.value;
  derivations.push(
    `Total encounters: ${totalEncounters.toLocaleString()} (${encounterEstimate.derivation})`,
  );

  // 2. Resolve payer mix
  const { payerMix, payerMetadata } = resolvePayerMix(orgType, tier1.payer_mix);
  derivations.push(
    `Payer mix resolved (${Object.values(payerMetadata).some((m) => m.source === "user_provided") ? "user + defaults" : "archetype defaults"})`,
  );

  // 2b. Apply geographic payer mix adjustments (state + setting type).
  // Geo multipliers are applied only to archetype-defaulted keys; any key the
  // user explicitly provided is protected so re-estimation never compounds the
  // adjustment on top of a value the user already set (which would cause drift
  // and break the slider lock control).
  const userProvidedPayerKeys = new Set(
    Object.entries(payerMetadata)
      .filter(([, m]) => m.source === "user_provided")
      .map(([k]) => k.replace("payer_mix.", "")),
  );
  const geoPayerAdj = applyPayerMixAdjustments(
    payerMix,
    tier1.state,
    tier1.setting_type,
    tier1.teaching_status,
    userProvidedPayerKeys,
  );
  derivations.push(...geoPayerAdj.derivations);

  // 3. Resolve specialty mix
  const { specialtyMix } = resolveSpecialtyMix(
    orgType,
    tier1.selected_specialties,
  );
  derivations.push(
    `Specialty mix: ${Object.keys(specialtyMix).length} specialties`,
  );

  // 4. Call deriveProfileData for base profile (encounter distribution + workforce)
  const userOverrides: Partial<ProfileData> = {
    encounter_volumes: tier1.encounter_volumes as
      | ProfileData["encounter_volumes"]
      | undefined,
    payer_mix: payerMix as ProfileData["payer_mix"],
    specialty_mix: specialtyMix,
  };

  const { profile, derivations: baseDerivations } = deriveProfileData(
    orgType,
    totalEncounters,
    tier1.licensed_beds ?? 0,
    userOverrides,
    tier1.sub_breakdowns,
  );

  for (const d of baseDerivations) {
    derivations.push(`${d.field}: ${d.explanation}`);
  }

  // 5. Enrich workforce with scheduling + denial_management roles
  const enrichedWorkforce = estimateWorkforce(
    orgType,
    totalEncounters,
    tier1.physician_count,
    profile.workforce,
  );
  profile.workforce = enrichedWorkforce.workforce;
  derivations.push(...enrichedWorkforce.derivations);

  // 6. Estimate operational baselines → baselineMap
  const baselineEstimate = estimateBaselines(
    orgType,
    totalEncounters,
    payerMix,
    specialtyMix,
    tier1.net_patient_revenue,
    tier1.licensed_beds,
    tier1.encounter_volumes,
  );
  derivations.push(...baselineEstimate.derivations);

  // 7. Apply geographic adjustments (mutates baselineMap)
  const geoAdj = applyGeographicAdjustments(
    tier1,
    profile,
    baselineEstimate.baselineMap,
  );
  derivations.push(...geoAdj.derivations);

  // 8. Purge org_context keys (e.g. legacy "bed_count") from the baseline map.
  //    These values now live on the org profile itself (bedCount, annualRevenue,
  //    etc.) and are injected by buildFormulaContext via extraContext.  Keeping
  //    them as baseline records causes stale data to shadow the authoritative
  //    org-profile value after a customer edits the field in Identity & Settings.
  const orgContextKeys = new Set<string>();
  for (const def of getOrgContextDefinitions()) {
    orgContextKeys.add(def.key);
    for (const alias of def.aliases ?? []) {
      orgContextKeys.add(alias);
    }
  }
  for (const key of orgContextKeys) {
    delete baselineEstimate.baselineMap[key];
  }

  // 9. Convert baselineMap → BaselineRecord[]
  const baselineRecords = toBaselineRecords(baselineEstimate.baselineMap);

  // 10. Compute completeness
  const completeness = computeCompleteness(tier1);
  derivations.push(`Completeness score: ${completeness}%`);

  return {
    profile,
    baselineRecords,
    completeness,
    derivations,
  };
}

// ── Sub-functions ───────────────────────────────────────────

/**
 * Priority: user total > user encounter volumes > beds × encounters_per_bed >
 * physicians × enc_per_physician > median fallback.
 */
export function estimateTotalEncounters(tier1: Tier1Inputs): EstimatedValue {
  const orgType = tier1.org_type;
  const bench = ORG_BENCHMARKS[orgType];

  // Priority 1: User provided total encounters
  if (tier1.total_encounters && tier1.total_encounters > 0) {
    return {
      value: tier1.total_encounters,
      source: "user_provided",
      derivation: "User-provided total encounters",
      confidence: "high",
    };
  }

  // Priority 2: Sum user-provided inpatient + outpatient encounter volumes
  const encounterVolumeTotal = sumTotalEncounterVolumes(
    tier1.encounter_volumes,
  );
  if (encounterVolumeTotal != null && encounterVolumeTotal > 0) {
    return {
      value: encounterVolumeTotal,
      source: "user_provided",
      derivation: "Sum of inpatient + outpatient encounter volumes",
      confidence: "high",
    };
  }

  // Priority 3: Derive from beds (not applicable for physician_group)
  if (
    tier1.licensed_beds &&
    tier1.licensed_beds > 0 &&
    bench.encounters_per_bed > 0
  ) {
    const estimated = Math.round(
      tier1.licensed_beds * bench.encounters_per_bed,
    );
    return {
      value: estimated,
      source: "derived",
      derivation: `${tier1.licensed_beds} beds × ${bench.encounters_per_bed.toLocaleString()} enc/bed`,
      confidence: "moderate",
      benchmark_source: "CMS Provider of Services / industry benchmarks",
    };
  }

  // Priority 4: Derive from physician count
  if (
    tier1.physician_count &&
    tier1.physician_count > 0 &&
    bench.encounters_per_physician > 0
  ) {
    const estimated = Math.round(
      tier1.physician_count * bench.encounters_per_physician,
    );
    return {
      value: estimated,
      source: "derived",
      derivation: `${tier1.physician_count} physicians × ${bench.encounters_per_physician.toLocaleString()} enc/physician`,
      confidence: "moderate",
      benchmark_source: "MGMA benchmarks",
    };
  }

  // Priority 5: Median fallback
  return {
    value: bench.median_encounters,
    source: "benchmark",
    derivation: `Median for ${orgType.replace(/_/g, " ")}`,
    confidence: "low",
    benchmark_source: "AHA Annual Survey / CMS benchmarks",
  };
}

/**
 * Merge user-provided partial payer mix with archetype defaults, converting
 * user-provided **revenue shares** to encounter-weighted shares.
 *
 * Users enter payer mix as "% of revenue." Archetype defaults are already
 * encounter-weighted. To merge them correctly:
 *
 * 1. Defaults are converted to revenue space via {@link encounterToRevenueShares}.
 * 2. Non-overridden default revenue shares are scaled to fill the remaining
 *    share (`1 − Σ user_revenue_shares`), so a partial input like
 *    `{ medicaid: 0.8 }` is preserved at 80% of revenue with the other
 *    payers splitting the remaining 20% proportionally.
 * 3. The full composite (user + scaled defaults) is converted back to
 *    encounter space via {@link revenueToEncounterShares}.
 *
 * When no user keys are provided, archetype encounter-share defaults are
 * returned unchanged.
 *
 * After conversion and merge the result is normalized to sum to 1.0.
 */
export function resolvePayerMix(
  orgType: OrgType,
  userPartial?: Partial<Record<string, number>>,
): { payerMix: Record<string, number>; payerMetadata: FieldMetadata } {
  const archDefault = ARCHETYPES[orgType].payer_mix;
  const metadata: FieldMetadata = {};

  if (!userPartial || Object.keys(userPartial).length === 0) {
    // All archetype defaults (already encounter-weighted)
    for (const [key, val] of Object.entries(archDefault)) {
      metadata[`payer_mix.${key}`] = {
        value: val,
        source: "archetype_default",
        derivation: `Default for ${orgType.replace(/_/g, " ")}`,
        confidence: "moderate",
      };
    }
    return { payerMix: { ...archDefault }, payerMetadata: metadata };
  }

  // ── Convert user-provided revenue shares → encounter shares ──────────
  //
  // To preserve correct relative proportions we must convert user-provided
  // and archetype-default keys *together*. Converting only the user-provided
  // subset in isolation would renormalize them to 1.0, inflating a single
  // partial override (e.g. { medicare_ffs: 0.3 } → 1.0) before it gets
  // merged with defaults.
  //
  // Archetype defaults are already encounter-weighted. User values are
  // revenue shares. We first convert the defaults into revenue space so
  // the composite vector is uniformly revenue-weighted, then convert the
  // whole thing back to encounter space in one pass. The round-trip is
  // exact for non-overridden keys (enc → rev → enc) so defaults that the
  // user didn't touch stay at their archetype values.
  const userRevenueKeys = new Set<string>();
  const userValues: Record<string, number> = {};
  for (const [key, val] of Object.entries(userPartial)) {
    if (val !== undefined && val !== null) {
      userValues[key] = val;
      userRevenueKeys.add(key);
    }
  }

  // Convert archetype encounter-share defaults to revenue space, then scale
  // non-overridden defaults so that user keys + scaled defaults sum to 1.0.
  // This ensures a partial override like { medicaid: 0.8 } is not diluted by
  // normalized defaults that still sum to ~1.0 on their own.
  const defaultRevenueShares = encounterToRevenueShares(archDefault);
  const userSum = Object.values(userValues).reduce((a, b) => a + b, 0);
  const remainingForDefaults = Math.max(0, 1 - userSum);

  // Sum of default revenue shares for keys NOT overridden by the user
  const defaultNonOverriddenSum = Object.entries(defaultRevenueShares)
    .filter(([key]) => !userRevenueKeys.has(key))
    .reduce((s, [, v]) => s + v, 0);

  const compositeRevenueShares: Record<string, number> = {};
  for (const [key, val] of Object.entries(defaultRevenueShares)) {
    if (userRevenueKeys.has(key)) {
      compositeRevenueShares[key] = userValues[key] ?? val;
    } else {
      // Scale non-overridden defaults to fill the remaining share
      compositeRevenueShares[key] =
        defaultNonOverriddenSum > 0
          ? (val / defaultNonOverriddenSum) * remainingForDefaults
          : 0;
    }
  }
  // Add any user keys that don't exist in archDefault
  for (const key of userRevenueKeys) {
    if (!(key in defaultRevenueShares)) {
      compositeRevenueShares[key] = userValues[key] ?? 0;
    }
  }

  // Convert the full composite vector (user + defaults) at once
  const convertedShares =
    userRevenueKeys.size > 0
      ? revenueToEncounterShares(compositeRevenueShares)
      : {};

  // When the user provided at least one key, use the fully-converted shares
  // for all keys (user-provided and default alike).
  const merged: Record<string, number> = {};
  if (userRevenueKeys.size > 0) {
    for (const key of Object.keys(archDefault)) {
      merged[key] = convertedShares[key] ?? 0;
      if (userRevenueKeys.has(key)) {
        metadata[`payer_mix.${key}`] = {
          value: merged[key] ?? 0,
          source: "user_provided",
          derivation:
            "User-provided revenue share → encounter share via benchmark rev/encounter",
          confidence: "high",
        };
      } else {
        metadata[`payer_mix.${key}`] = {
          value: merged[key] ?? 0,
          source: "archetype_default",
          derivation: `Default for ${orgType.replace(/_/g, " ")} (converted with user-provided mix)`,
          confidence: "moderate",
        };
      }
    }
  } else {
    for (const key of Object.keys(archDefault)) {
      merged[key] = archDefault[key] ?? 0;
      metadata[`payer_mix.${key}`] = {
        value: archDefault[key] ?? 0,
        source: "archetype_default",
        derivation: `Default for ${orgType.replace(/_/g, " ")}`,
        confidence: "moderate",
      };
    }
  }

  // Normalize to sum to 1.0
  const sum = Object.values(merged).reduce((a, b) => a + b, 0);
  if (sum > 0 && Math.abs(sum - 1.0) > 0.001) {
    for (const key of Object.keys(merged)) {
      merged[key] = (merged[key] ?? 0) / sum;
      const meta = metadata[`payer_mix.${key}`];
      if (meta) meta.value = merged[key] ?? 0;
    }
  }

  return { payerMix: merged, payerMetadata: metadata };
}

/**
 * Filter archetype specialty weights to checked specialties, normalize to 1.0.
 */
export function resolveSpecialtyMix(
  orgType: OrgType,
  selectedSpecialties?: string[],
): { specialtyMix: Record<string, number>; specialtyMetadata: FieldMetadata } {
  const metadata: FieldMetadata = {};

  if (!selectedSpecialties || selectedSpecialties.length === 0) {
    // Use full archetype specialty mix
    const archMix = ARCHETYPES[orgType].specialty_mix;
    for (const [key, val] of Object.entries(archMix)) {
      metadata[`specialty_mix.${key}`] = {
        value: val,
        source: "archetype_default",
        derivation: `Default specialty weight for ${orgType.replace(/_/g, " ")}`,
        confidence: "moderate",
      };
    }
    return { specialtyMix: { ...archMix }, specialtyMetadata: metadata };
  }

  // Build mix from selected specialties using SPECIALTY_DEFINITIONS weights
  const raw: Record<string, number> = {};
  for (const specKey of selectedSpecialties) {
    const def = SPECIALTY_DEFINITIONS.find((s) => s.key === specKey);
    const weight = def?.org_weights[orgType] ?? 0.05; // Unknown specialties get 0.05
    raw[specKey] = weight;
  }

  // Normalize to 1.0
  const sum = Object.values(raw).reduce((a, b) => a + b, 0);
  const normalized: Record<string, number> = {};
  for (const [key, val] of Object.entries(raw)) {
    normalized[key] = sum > 0 ? val / sum : 1 / selectedSpecialties.length;
    metadata[`specialty_mix.${key}`] = {
      value: normalized[key],
      source: "derived",
      derivation: `Selected specialty, weight normalized from ${orgType.replace(/_/g, " ")} benchmarks`,
      confidence: "moderate",
    };
  }

  return { specialtyMix: normalized, specialtyMetadata: metadata };
}

/**
 * Enrich base workforce with scheduling + denial_management roles.
 * Physician count from user takes precedence.
 */
export function estimateWorkforce(
  orgType: OrgType,
  totalEncounters: number,
  physicianCount: number | undefined,
  baseWorkforce: ProfileData["workforce"],
): {
  workforce: ProfileData["workforce"];
  metadata: FieldMetadata;
  derivations: string[];
} {
  const ratios = WORKFORCE_RATIOS[orgType];
  const metadata: FieldMetadata = {};
  const derivations: string[] = [];
  const workforce = { ...baseWorkforce };

  // Override physician count if user-provided
  if (physicianCount && physicianCount > 0 && workforce.physician) {
    workforce.physician = {
      ...workforce.physician,
      ftes: physicianCount,
    };
    metadata["workforce.physician.ftes"] = {
      value: physicianCount,
      source: "user_provided",
      derivation: "User-provided physician count",
      confidence: "high",
    };
    derivations.push(`Physician FTEs: ${physicianCount} (user-provided)`);
  }

  // Add scheduling role if not present
  if (!workforce.scheduling && ratios.scheduling) {
    const ftes =
      Math.round(
        (totalEncounters / 10_000) *
          ratios.scheduling.ftes_per_10k_encounters *
          10,
      ) / 10;
    workforce.scheduling = {
      ftes,
      avg_fully_loaded_cost: ratios.scheduling.avg_cost,
    };
    metadata["workforce.scheduling.ftes"] = {
      value: ftes,
      source: "benchmark",
      derivation: `${ratios.scheduling.ftes_per_10k_encounters} per 10K encounters`,
      confidence: "moderate",
      benchmark_source: "MGMA staffing benchmarks",
    };
    derivations.push(`Scheduling FTEs: ${ftes} (benchmark-estimated)`);
  }

  // Add denial_management role if not present
  if (!workforce.denial_management && ratios.denial_management) {
    const ftes =
      Math.round(
        (totalEncounters / 10_000) *
          ratios.denial_management.ftes_per_10k_encounters *
          10,
      ) / 10;
    workforce.denial_management = {
      ftes,
      avg_fully_loaded_cost: ratios.denial_management.avg_cost,
    };
    metadata["workforce.denial_management.ftes"] = {
      value: ftes,
      source: "benchmark",
      derivation: `${ratios.denial_management.ftes_per_10k_encounters} per 10K encounters`,
      confidence: "moderate",
      benchmark_source: "HFMA staffing benchmarks",
    };
    derivations.push(`Denial management FTEs: ${ftes} (benchmark-estimated)`);
  }

  // Add new workforce roles (CDI, eligibility, revenue integrity, perioperative)
  const newRoles = [
    "cdi_specialist",
    "eligibility_verification",
    "revenue_integrity",
    "perioperative_admin",
  ] as const;
  for (const role of newRoles) {
    if (!workforce[role] && ratios[role]) {
      const ftes =
        Math.round(
          (totalEncounters / 10_000) *
            ratios[role].ftes_per_10k_encounters *
            10,
        ) / 10;
      workforce[role] = {
        ftes,
        avg_fully_loaded_cost: ratios[role].avg_cost,
      };
      metadata[`workforce.${role}.ftes`] = {
        value: ftes,
        source: "benchmark",
        derivation: `${ratios[role].ftes_per_10k_encounters} per 10K encounters`,
        confidence: "moderate",
        benchmark_source: "HFMA/AAPC staffing benchmarks",
      };
      derivations.push(
        `${role.replace(/_/g, " ")} FTEs: ${ftes} (benchmark-estimated)`,
      );
    }
  }

  // Seed non-clinical template rows (Exec & admin, IT / informatics,
  // Facilities / operations) as empty entries. These have no benchmark data
  // (isBenchmark: false) — SMEs fill them in. Group comes from the registry.
  for (const def of WORKFORCE_ROLE_DEFS) {
    if (!def.isBenchmark && !workforce[def.key]) {
      workforce[def.key] = {
        ftes: 0,
        avg_fully_loaded_cost: 0,
        group: def.group,
      };
    }
  }

  return { workforce, metadata, derivations };
}

/**
 * Estimate operational baselines from payer mix, specialty mix, encounter volume, and optional revenue.
 * Returns a baselineMap (key → EstimatedBaselineValue) instead of flat baselines + metadata.
 */
export function estimateBaselines(
  orgType: OrgType,
  totalEncounters: number,
  payerMix: Record<string, number>,
  specialtyMix: Record<string, number>,
  netRevenue?: number,
  bedCount?: number,
  encounterVolumes?: Partial<Record<string, number>>,
): {
  baselineMap: Record<string, EstimatedBaselineValue>;
  derivations: string[];
} {
  const derivations: string[] = [];
  const baselineMap: Record<string, EstimatedBaselineValue> = {};
  const bench = ORG_BENCHMARKS[orgType];

  // PA volume from payer × encounter volume
  let paVolume = 0;
  for (const [payer, share] of Object.entries(payerMix)) {
    const rate = PA_RATE_PER_ENCOUNTER[payer] ?? 0.03;
    paVolume += totalEncounters * share * rate;
  }
  paVolume = Math.round(paVolume);
  baselineMap.pa_volume_annual = {
    value: paVolume,
    source: "estimated",
    sourceLabel: "Sum of encounters × payer share × PA rate per payer",
  };
  derivations.push(`PA volume: ${paVolume.toLocaleString()} (payer-weighted)`);

  // Overall denial rate (payer-weighted)
  let denialRate = 0;
  for (const [payer, share] of Object.entries(payerMix)) {
    denialRate += share * (PA_DENIAL_RATE_BY_PAYER[payer] ?? 0.05);
  }
  baselineMap.overall_denial_rate = {
    value: Math.round(denialRate * 1000) / 1000,
    source: "estimated",
    sourceLabel: "Payer-weighted average denial rate",
  };

  // ── Single-pass specialty weighting ────────────────────────
  // Compute all specialty-weighted values in one loop over specialtyMix
  const defaultBench =
    SPECIALTY_BENCHMARKS._default ??
    SPECIALTY_BENCHMARKS[Object.keys(SPECIALTY_BENCHMARKS)[0] ?? ""];
  let weightedNoshowRate = 0;
  let weightedRevenueMultiplier = 0;
  let weightedApptValue = 0;
  let weightedBurnout = 0;
  let weightedCancellation = 0;
  let weightedDocTime = 0;
  let weightedSurgicalFraction = 0;

  for (const [spec, weight] of Object.entries(specialtyMix)) {
    const b = SPECIALTY_BENCHMARKS[spec] ?? defaultBench;
    if (!b) continue;
    weightedNoshowRate += weight * b.noshowRate;
    weightedRevenueMultiplier += weight * b.revenueMultiplier;
    weightedApptValue += weight * b.appointmentValue;
    weightedBurnout += weight * b.burnoutRate;
    weightedCancellation += weight * b.cancellationRate;
    weightedDocTime += weight * b.docTimeMinutes;
    weightedSurgicalFraction += weight * b.surgicalFraction;
  }

  // No-show rate (specialty-weighted, then payer-adjusted)
  let payerNoshowMultiplier = 0;
  for (const [payer, share] of Object.entries(payerMix)) {
    payerNoshowMultiplier += share * (NOSHOW_RATE_BY_PAYER[payer] ?? 1.0);
  }
  const noshowRate = weightedNoshowRate * payerNoshowMultiplier;
  baselineMap.no_show_rate = {
    value: Math.round(noshowRate * 1000) / 1000,
    source: "estimated",
    sourceLabel: "Specialty-weighted × payer-adjusted no-show rate",
  };

  // Appointments from encounters
  const appointmentRatio = orgType === "physician_group" ? 0.85 : 0.7;
  baselineMap.annual_appointments = {
    value: Math.round(totalEncounters * appointmentRatio),
    source: "estimated",
    sourceLabel: `${(appointmentRatio * 100).toFixed(0)}% of total encounters`,
  };

  // Call volume from encounters
  const callRatio = orgType === "physician_group" ? 0.45 : 0.6;
  baselineMap.call_volume_annual = {
    value: Math.round(totalEncounters * callRatio),
    source: "estimated",
    sourceLabel: `${(callRatio * 100).toFixed(0)}% of total encounters`,
  };

  // Average claim value (from revenue, or payer-weighted + specialty-adjusted)
  let avgClaimValue: number;
  const revenueProvided = netRevenue != null && netRevenue > 0;
  if (revenueProvided) {
    avgClaimValue = Math.round(netRevenue / totalEncounters);
  } else {
    // Payer-weighted average revenue per encounter
    let weighted = 0;
    for (const [payer, share] of Object.entries(payerMix)) {
      weighted +=
        share *
        (AVG_REVENUE_PER_ENCOUNTER_BY_PAYER[payer] ??
          bench.avg_revenue_per_encounter);
    }
    avgClaimValue = Math.round(weighted);
  }

  // Apply specialty revenue multiplier (only when revenue is not user-provided)
  if (!revenueProvided) {
    avgClaimValue = Math.round(avgClaimValue * weightedRevenueMultiplier);
  }

  baselineMap.avg_claim_value = {
    value: avgClaimValue,
    source: revenueProvided ? "estimated" : "benchmark",
    sourceLabel: revenueProvided
      ? `Net revenue $${netRevenue.toLocaleString()} ÷ ${totalEncounters.toLocaleString()} encounters`
      : "Payer-weighted × specialty-adjusted revenue per encounter",
  };

  // Average denial value (80% of claim value — denials skew lower)
  baselineMap.avg_denial_value = {
    value: Math.round(avgClaimValue * 0.8),
    source: "estimated",
    sourceLabel: "80% of avg claim value",
  };

  // Average appointment value (from single-pass)
  baselineMap.avg_appointment_value = {
    value: Math.round(weightedApptValue),
    source: "estimated",
    sourceLabel: "Specialty-weighted appointment value",
  };

  // Fixed industry baselines (benchmark source)
  baselineMap.avg_pa_handling_time_minutes = {
    value: BASELINE_BENCHMARKS.avg_pa_handling_time_minutes,
    source: "benchmark",
    sourceLabel: "CAQH Index",
  };
  // Physician burnout rate (from single-pass)
  baselineMap.physician_burnout_rate = {
    value: Math.round(weightedBurnout * 1000) / 1000,
    source: "estimated",
    sourceLabel: "Specialty-weighted burnout rate",
  };
  baselineMap.avg_call_handle_time_minutes = {
    value: BASELINE_BENCHMARKS.avg_call_handle_time_minutes,
    source: "benchmark",
    sourceLabel: "HCAHPS / industry benchmark",
  };
  baselineMap.call_abandonment_rate = {
    value: BASELINE_BENCHMARKS.call_abandonment_rate,
    source: "benchmark",
    sourceLabel: "Healthcare call center benchmark",
  };
  baselineMap.denial_recovery_rate = {
    value: BASELINE_BENCHMARKS.denial_recovery_rate,
    source: "benchmark",
    sourceLabel: "HFMA denial management benchmark",
  };
  // Rework cost scales with claim complexity — base $57.23 adjusted by claim value ratio
  const claimValueRatio = avgClaimValue / 3000; // normalized to ~average claim value
  const adjustedReworkCost =
    Math.round(
      BASELINE_BENCHMARKS.denial_rework_cost *
        Math.max(0.7, Math.min(1.5, claimValueRatio)) *
        100,
    ) / 100;
  baselineMap.denial_rework_cost = {
    value: adjustedReworkCost,
    source: "estimated",
    sourceLabel: `Base $${BASELINE_BENCHMARKS.denial_rework_cost} × claim complexity factor`,
  };
  // Cancellation rate (from single-pass)
  baselineMap.cancellation_rate = {
    value: Math.round(weightedCancellation * 1000) / 1000,
    source: "estimated",
    sourceLabel: "Specialty-weighted cancellation rate",
  };
  baselineMap.current_backfill_rate = {
    value: BASELINE_BENCHMARKS.current_backfill_rate,
    source: "benchmark",
    sourceLabel: "Healthcare scheduling benchmark",
  };
  // Documentation time (from single-pass)
  baselineMap.avg_doc_time_per_encounter_minutes = {
    value: Math.round(weightedDocTime * 10) / 10,
    source: "estimated",
    sourceLabel: "Specialty-weighted documentation time",
  };
  baselineMap.coding_accuracy_rate = {
    value: BASELINE_BENCHMARKS.coding_accuracy_rate,
    source: "benchmark",
    sourceLabel: "AHIMA coding accuracy benchmark",
  };
  baselineMap.pa_approval_rate = {
    value: BASELINE_BENCHMARKS.pa_approval_rate,
    source: "benchmark",
    sourceLabel: "CAQH Index",
  };
  baselineMap.pa_turnaround_hours = {
    value: BASELINE_BENCHMARKS.pa_turnaround_hours,
    source: "benchmark",
    sourceLabel: "CAQH Index",
  };
  baselineMap.avg_wrvus_per_physician_per_month = {
    value: BASELINE_BENCHMARKS.avg_wrvus_per_physician_per_month,
    source: "benchmark",
    sourceLabel: "MGMA wRVU benchmark",
  };

  // Coding-specific denial rate (lower than overall)
  baselineMap.coding_denial_rate = {
    value:
      Math.round(baselineMap.overall_denial_rate.value * 0.4 * 1000) / 1000,
    source: "estimated",
    sourceLabel: "40% of overall denial rate",
  };

  // Weighted avg coding time — uses the mutually-exclusive leaf distribution so
  // the per-setting weights sum to 1.0 (a true average). Using the overlapping
  // distribution here would double-count outpatient sub-breakdowns and inflate
  // the result.
  const archetype = ARCHETYPES[orgType];
  const codingTimeDistribution = archetypeLeafEncounterDistribution(archetype);
  let weightedCodingTime = 0;
  for (const [setting, pct] of Object.entries(codingTimeDistribution)) {
    weightedCodingTime += pct * (CODING_TIME_MINUTES[setting] ?? 10);
  }
  baselineMap.weighted_avg_coding_time = {
    value: Math.round(weightedCodingTime * 10) / 10,
    source: "estimated",
    sourceLabel: "Setting-weighted coding time from encounter distribution",
  };

  // Per-setting coding time (consumed by autonomous-coding.ts)
  baselineMap.avg_coding_time_minutes_ip = {
    value: CODING_TIME_MINUTES.inpatient ?? 20,
    source: "benchmark",
    sourceLabel: "AHIMA coding time benchmark",
  };
  baselineMap.avg_coding_time_minutes_op = {
    value: CODING_TIME_MINUTES.outpatient ?? 10,
    source: "benchmark",
    sourceLabel: "AHIMA coding time benchmark",
  };
  baselineMap.avg_coding_time_minutes_ed = {
    value: CODING_TIME_MINUTES.emergency ?? 8,
    source: "benchmark",
    sourceLabel: "AHIMA coding time benchmark",
  };
  baselineMap.avg_coding_time_minutes_rad = {
    value: CODING_TIME_MINUTES.radiology ?? 4,
    source: "benchmark",
    sourceLabel: "AHIMA coding time benchmark",
  };

  // ── CDI Baselines ──────────────────────────────────────
  const directInpatientVolume = finiteNonNegativeEncounterVolume(
    encounterVolumes?.inpatient,
  );
  baselineMap.inpatient_discharges = {
    value:
      directInpatientVolume != null
        ? Math.round(directInpatientVolume)
        : Math.round(
            (archetype.primary_volume_distribution.inpatient ?? 0.06) *
              totalEncounters,
          ),
    source: directInpatientVolume != null ? "customer_reported" : "estimated",
    sourceLabel:
      directInpatientVolume != null
        ? "Org profile inpatient encounter volume"
        : "Inpatient share of total encounters",
  };
  baselineMap.current_cmi = {
    value: CURRENT_CMI[orgType],
    source: "benchmark",
    sourceLabel: "CMS case mix index benchmark",
  };
  baselineMap.cdi_review_rate = {
    value: BASELINE_BENCHMARKS.cdi_review_rate,
    source: "benchmark",
    sourceLabel: "ACDIS CDI review benchmark",
  };
  baselineMap.documentation_driven_denial_rate = {
    value: BASELINE_BENCHMARKS.documentation_driven_denial_rate,
    source: "benchmark",
    sourceLabel: "HFMA documentation denial benchmark",
  };

  // ── Patient Financial Clearance Baselines ──────────────
  baselineMap.self_pay_pct = {
    value: payerMix.self_pay ?? 0.06,
    source: "estimated",
    sourceLabel: "Self-pay share from payer mix",
  };
  baselineMap.registration_denial_rate = {
    value: BASELINE_BENCHMARKS.registration_denial_rate,
    source: "benchmark",
    sourceLabel: "HFMA registration denial benchmark",
  };
  baselineMap.avg_verification_time_minutes = {
    value: BASELINE_BENCHMARKS.avg_verification_time_minutes,
    source: "benchmark",
    sourceLabel: "Healthcare eligibility verification benchmark",
  };

  // ── Charge Capture Baselines ───────────────────────────
  baselineMap.missed_charge_rate_surgical = {
    value: BASELINE_BENCHMARKS.missed_charge_rate_surgical,
    source: "benchmark",
    sourceLabel: "HFMA charge capture benchmark",
  };
  baselineMap.missed_charge_rate_ed = {
    value: BASELINE_BENCHMARKS.missed_charge_rate_ed,
    source: "benchmark",
    sourceLabel: "HFMA charge capture benchmark",
  };
  baselineMap.missed_charge_rate_outpatient = {
    value: BASELINE_BENCHMARKS.missed_charge_rate_outpatient,
    source: "benchmark",
    sourceLabel: "HFMA charge capture benchmark",
  };
  baselineMap.charge_lag_days = {
    value: BASELINE_BENCHMARKS.charge_lag_days,
    source: "benchmark",
    sourceLabel: "HFMA charge capture benchmark",
  };

  // ── OR Optimization Baselines ──────────────────────────
  // Surgical volume (from single-pass weightedSurgicalFraction, floored by org-type share)
  const effectiveSurgicalShare = Math.max(
    SURGICAL_ENCOUNTER_SHARE[orgType],
    weightedSurgicalFraction,
  );
  const annualSurgicalCases = Math.round(
    finiteNonNegativeEncounterVolume(encounterVolumes?.surgical) ??
      totalEncounters * effectiveSurgicalShare,
  );
  const directSurgicalVolume = finiteNonNegativeEncounterVolume(
    encounterVolumes?.surgical,
  );
  const surgicalShareForRevenue =
    totalEncounters > 0 ? annualSurgicalCases / totalEncounters : 0;
  baselineMap.annual_surgical_cases = {
    value: annualSurgicalCases,
    source: directSurgicalVolume != null ? "customer_reported" : "estimated",
    sourceLabel:
      directSurgicalVolume != null
        ? "Org profile surgical encounter volume"
        : `${(effectiveSurgicalShare * 100).toFixed(1)}% of total encounters (max of org-type and specialty-weighted)`,
  };
  // Derive OR count: ~1 OR per 1,000 surgical cases/year
  const orCount =
    annualSurgicalCases > 0
      ? Math.max(1, Math.round(annualSurgicalCases / 1_000))
      : 0;
  baselineMap.or_count = {
    value: orCount,
    source: "estimated",
    sourceLabel: "~1 OR per 1,000 surgical cases/year",
  };
  baselineMap.or_utilization_rate = {
    value: OR_UTILIZATION_RATE[orgType],
    source: "benchmark",
    sourceLabel: "OR utilization benchmark by org type",
  };
  baselineMap.block_utilization_rate = {
    value: BASELINE_BENCHMARKS.block_utilization_rate,
    source: "benchmark",
    sourceLabel: "OR block utilization benchmark",
  };
  // Revenue per OR hour: derive from revenue / surgical hours, or benchmark
  if (netRevenue && annualSurgicalCases > 0) {
    // OR revenue = facility + professional fees during procedure only
    // (~40% of total surgical episode revenue is attributable to OR time)
    const surgicalRevenue = netRevenue * surgicalShareForRevenue * 0.4;
    const orHoursPerYear = orCount * 8 * 250 * OR_UTILIZATION_RATE[orgType];
    baselineMap.avg_revenue_per_or_hour = {
      value:
        orHoursPerYear > 0
          ? Math.round(surgicalRevenue / orHoursPerYear)
          : BASELINE_BENCHMARKS.avg_revenue_per_or_hour,
      source: orHoursPerYear > 0 ? "estimated" : "benchmark",
      sourceLabel:
        orHoursPerYear > 0
          ? "Surgical revenue ÷ OR hours/year"
          : "OR revenue benchmark",
    };
  } else {
    baselineMap.avg_revenue_per_or_hour = {
      value: BASELINE_BENCHMARKS.avg_revenue_per_or_hour,
      source: "benchmark",
      sourceLabel: "OR revenue benchmark",
    };
  }

  // ── V3: Payer-Specialty-Aware Baselines ──────────────────────

  // Denial pool: payer-weighted denial rate and revenue index
  const v3OrgDenialRate = computeOrgDenialRate(payerMix);
  baselineMap.org_denial_rate = {
    value: Math.round(v3OrgDenialRate * 1000) / 1000,
    source: "estimated",
    sourceLabel: "Payer-weighted org denial rate (V3)",
  };
  baselineMap.marginal_prevention_factor = {
    value:
      Math.round(computeMarginalPreventionFactor(v3OrgDenialRate) * 1000) /
      1000,
    source: "estimated",
    sourceLabel: "Marginal prevention factor from org denial rate",
  };
  baselineMap.denial_revenue_index = {
    value: Math.round(computeDenialRevenueIndex(payerMix) * 1000) / 1000,
    source: "estimated",
    sourceLabel: "Payer-weighted denial revenue index",
  };

  // No-show pool: payer-weighted revenue index + specialty value multiplier
  baselineMap.noshow_revenue_index = {
    value: Math.round(computeNoshowRevenueIndex(payerMix) * 1000) / 1000,
    source: "estimated",
    sourceLabel: "Payer-weighted no-show revenue index",
  };
  baselineMap.specialty_noshow_value_multiplier = {
    value:
      Math.round(computeSpecialtyWeightedNoshowValue(specialtyMix) * 1000) /
      1000,
    source: "estimated",
    sourceLabel: "Specialty-weighted no-show value multiplier",
  };

  // Patient financial clearance: net discovery value
  baselineMap.net_discovery_index = {
    value: Math.round(computeNetDiscoveryIndex() * 1000) / 1000,
    source: "estimated",
    sourceLabel: "Net discovery index for financial clearance",
  };
  baselineMap.self_pay_setting_adjustment = {
    value:
      Math.round(
        computeSettingAdjustment(archetypeEncounterDistribution(archetype)) *
          1000,
      ) / 1000,
    source: "estimated",
    sourceLabel: "Self-pay setting adjustment from encounter distribution",
  };

  // Ambient documentation: specialty-weighted physician cost
  baselineMap.ambient_weighted_physician_cost = {
    value: Math.round(computeAmbientWeightedPhysicianCost(specialtyMix)),
    source: "estimated",
    sourceLabel: "Specialty-weighted physician cost for ambient documentation",
  };

  // Autonomous coding: weighted inpatient coding time
  baselineMap.weighted_ip_coding_time = {
    value:
      Math.round(
        computeWeightedInpatientCodingTime(
          specialtyMix,
          CODING_TIME_MINUTES.inpatient ?? 0,
        ) * 10,
      ) / 10,
    source: "estimated",
    sourceLabel: "Specialty-weighted inpatient coding time",
  };

  // OR optimization: surgical revenue index
  baselineMap.surgical_revenue_index = {
    value: Math.round(computeSurgicalRevenueIndex(payerMix) * 1000) / 1000,
    source: "estimated",
    sourceLabel: "Payer-weighted surgical revenue index",
  };

  // CDI: CMI durability factor
  baselineMap.cmi_durability = {
    value: computeCMIDurability(CURRENT_CMI[orgType] ?? 0, orgType),
    source: "estimated",
    sourceLabel: "CMI durability factor from org type and CMI",
  };

  // PA automation: weighted contribution margin
  baselineMap.weighted_contribution_margin = {
    value: Math.round(computeWeightedContributionMargin() * 1000) / 1000,
    source: "estimated",
    sourceLabel: "Weighted contribution margin for PA automation",
  };

  // QA review rate (default to moderate; category defaults may override per scenario)
  baselineMap.qa_review_rate = {
    value: QA_REVIEW_RATE.moderate ?? 0,
    source: "benchmark",
    sourceLabel: "QA review rate benchmark (moderate)",
  };

  // Call resolution rate (default to moderate)
  baselineMap.call_resolution_rate = {
    value: CALL_RESOLUTION_RATE.moderate ?? 0,
    source: "benchmark",
    sourceLabel: "Call resolution rate benchmark (moderate)",
  };

  derivations.push(
    `V3 payer-specialty baselines: denial_rev_idx=${baselineMap.denial_revenue_index.value}, ` +
      `noshow_rev_idx=${baselineMap.noshow_revenue_index.value}, ` +
      `net_discovery=${baselineMap.net_discovery_index.value}, ` +
      `cmi_durability=${baselineMap.cmi_durability.value}`,
  );

  return { baselineMap, derivations };
}

/**
 * Apply state-level and setting-type payer mix adjustments.
 * Multiplies payer shares by geographic factors, then re-normalizes.
 * Mutates the payerMix record in place.
 *
 * @param payerMix
 * @param state
 * @param settingType
 * @param teachingStatus
 * @param protectedKeys - Keys whose values must not be changed by geo
 *   adjustments (typically user-provided overrides). Non-protected keys are
 *   adjusted and then renormalized to fill the share left by protected keys,
 *   so the full mix still sums to 1.0.
 */
export function applyPayerMixAdjustments(
  payerMix: Record<string, number>,
  state?: string,
  settingType?: "urban" | "suburban" | "rural",
  teachingStatus?: boolean,
  protectedKeys?: Set<string>,
): { derivations: string[] } {
  const derivations: string[] = [];
  let applied = false;

  const isProtected = (key: string) => protectedKeys?.has(key) ?? false;

  // State-level adjustments
  if (state) {
    const stateAdj = STATE_PAYER_MIX_ADJUSTMENTS[state];
    if (stateAdj) {
      for (const [key, multiplier] of Object.entries(stateAdj)) {
        if (payerMix[key] != null && multiplier != null && !isProtected(key)) {
          payerMix[key] *= multiplier;
        }
      }
      applied = true;
      derivations.push(`Payer mix adjusted for state ${state}`);
    }
  }

  // Setting-type adjustments
  if (settingType) {
    const settingAdj = SETTING_TYPE_ADJUSTMENTS[settingType].payer_adjustments;
    if (settingAdj && Object.keys(settingAdj).length > 0) {
      for (const [key, multiplier] of Object.entries(settingAdj)) {
        if (payerMix[key] != null && multiplier != null && !isProtected(key)) {
          payerMix[key] *= multiplier;
        }
      }
      applied = true;
      derivations.push(`Payer mix adjusted for ${settingType} setting`);
    }
  }

  // Teaching hospital payer adjustments
  if (teachingStatus) {
    const teachAdj = TEACHING_ADJUSTMENTS.payer_adjustments;
    for (const [key, multiplier] of Object.entries(teachAdj)) {
      if (payerMix[key] != null && !isProtected(key)) {
        payerMix[key] *= multiplier;
      }
    }
    applied = true;
    derivations.push("Payer mix adjusted for teaching hospital status");
  }

  // Re-normalize. Protected keys keep their exact values; non-protected keys
  // are scaled so that the whole mix sums to 1.0.
  if (applied) {
    const adjustedKeys = Object.keys(payerMix).filter((k) => !isProtected(k));
    const protectedSum = Object.keys(payerMix)
      .filter((k) => isProtected(k))
      .reduce((s, k) => s + (payerMix[k] ?? 0), 0);
    const adjustedSum = adjustedKeys.reduce(
      (s, k) => s + (payerMix[k] ?? 0),
      0,
    );
    const remaining = Math.max(0, 1 - protectedSum);

    if (adjustedSum > 0 && remaining > 0) {
      const scale = remaining / adjustedSum;
      for (const key of adjustedKeys) {
        payerMix[key] = (payerMix[key] ?? 0) * scale;
      }
    } else if (adjustedSum > 0) {
      // Protected keys already fill 100% — zero out the rest
      for (const key of adjustedKeys) {
        payerMix[key] = 0;
      }
    } else if (remaining > 0 && adjustedKeys.length > 0) {
      // All adjusted keys started at 0 — split remaining share equally
      const each = remaining / adjustedKeys.length;
      for (const key of adjustedKeys) {
        payerMix[key] = each;
      }
    }
  }

  return { derivations };
}

/**
 * Apply geographic adjustments (state wage index, setting type, teaching status)
 * to workforce costs, no-show rates, and CMI.
 * Mutates baselineMap values instead of profile.baselines.
 */
function applyGeographicAdjustments(
  tier1: Tier1Inputs,
  profile: ProfileData,
  baselineMap: Record<string, EstimatedBaselineValue>,
): { derivations: string[] } {
  const derivations: string[] = [];

  // Compute composite wage multiplier
  let wageMultiplier = 1.0;
  const wageComponents: string[] = [];

  if (tier1.state) {
    const stateIdx = STATE_WAGE_INDEX[tier1.state] ?? 1.0;
    wageMultiplier *= stateIdx;
    wageComponents.push(`${tier1.state} GPCI=${stateIdx}`);
  }

  if (tier1.setting_type) {
    const adj = SETTING_TYPE_ADJUSTMENTS[tier1.setting_type];
    wageMultiplier *= adj.wage_multiplier;
    wageComponents.push(`${tier1.setting_type} ×${adj.wage_multiplier}`);

    // Adjust no-show rate
    if (adj.noshow_multiplier !== 1.0 && baselineMap.no_show_rate) {
      const origNoshow = baselineMap.no_show_rate.value;
      baselineMap.no_show_rate = {
        value: Math.round(origNoshow * adj.noshow_multiplier * 1000) / 1000,
        source: "estimated",
        sourceLabel: `Specialty × payer-weighted rate × ${tier1.setting_type} adjustment (×${adj.noshow_multiplier})`,
      };
      derivations.push(
        `No-show rate adjusted for ${tier1.setting_type} setting: ${(baselineMap.no_show_rate.value * 100).toFixed(1)}%`,
      );
    }

    // Adjust denial rates by setting type
    if (adj.denial_rate_multiplier !== 1.0) {
      for (const key of [
        "overall_denial_rate",
        "coding_denial_rate",
      ] as const) {
        if (baselineMap[key]) {
          baselineMap[key] = {
            ...baselineMap[key],
            value:
              Math.round(
                baselineMap[key].value * adj.denial_rate_multiplier * 1000,
              ) / 1000,
            sourceLabel: `${baselineMap[key].sourceLabel} × ${tier1.setting_type} adjustment`,
          };
        }
      }
      derivations.push(
        `Denial rates adjusted for ${tier1.setting_type} setting (×${adj.denial_rate_multiplier})`,
      );
    }

    // Apply encounter multiplier to volume baselines
    if (adj.encounter_multiplier !== 1.0) {
      const directEncounterVolumeKeys = new Set<string>();
      if (
        finiteNonNegativeEncounterVolume(tier1.encounter_volumes?.inpatient) !=
        null
      ) {
        directEncounterVolumeKeys.add("inpatient_discharges");
      }
      if (
        finiteNonNegativeEncounterVolume(tier1.encounter_volumes?.surgical) !=
        null
      ) {
        directEncounterVolumeKeys.add("annual_surgical_cases");
      }

      for (const key of [
        "annual_appointments",
        "call_volume_annual",
        "inpatient_discharges",
        "annual_surgical_cases",
        "pa_volume_annual",
      ]) {
        if (directEncounterVolumeKeys.has(key)) continue;
        if (baselineMap[key]) {
          baselineMap[key] = {
            ...baselineMap[key],
            value: Math.round(
              baselineMap[key].value * adj.encounter_multiplier,
            ),
            sourceLabel: `${baselineMap[key].sourceLabel} × ${tier1.setting_type} encounter adjustment`,
          };
        }
      }
      derivations.push(
        `Volume baselines adjusted for ${tier1.setting_type} setting (×${adj.encounter_multiplier})`,
      );
    }
  }

  if (tier1.teaching_status) {
    wageMultiplier *= TEACHING_ADJUSTMENTS.wage_multiplier;
    wageComponents.push(`teaching ×${TEACHING_ADJUSTMENTS.wage_multiplier}`);

    // Adjust CMI for teaching status
    if (baselineMap.current_cmi) {
      baselineMap.current_cmi = {
        value:
          Math.round(
            (baselineMap.current_cmi.value + TEACHING_ADJUSTMENTS.cmi_uplift) *
              100,
          ) / 100,
        source: "estimated",
        sourceLabel: `Base CMI + ${TEACHING_ADJUSTMENTS.cmi_uplift} teaching uplift`,
      };
      derivations.push(
        `CMI adjusted for teaching status: ${baselineMap.current_cmi.value}`,
      );
    }

    // Adjust coding complexity
    if (baselineMap.weighted_avg_coding_time) {
      baselineMap.weighted_avg_coding_time = {
        value:
          Math.round(
            baselineMap.weighted_avg_coding_time.value *
              TEACHING_ADJUSTMENTS.coding_complexity *
              10,
          ) / 10,
        source: "estimated",
        sourceLabel: `Coding time × ${TEACHING_ADJUSTMENTS.coding_complexity} teaching complexity factor`,
      };
      derivations.push(
        `Coding time adjusted for teaching complexity: ${baselineMap.weighted_avg_coding_time.value} min`,
      );
    }
    for (const key of ["overall_denial_rate", "coding_denial_rate"] as const) {
      if (baselineMap[key]) {
        baselineMap[key] = {
          ...baselineMap[key],
          value:
            Math.round(
              baselineMap[key].value *
                TEACHING_ADJUSTMENTS.denial_rate_multiplier *
                1000,
            ) / 1000,
          sourceLabel: `${baselineMap[key].sourceLabel} × teaching complexity`,
        };
      }
    }
    derivations.push(
      `Denial rates adjusted for teaching status (×${TEACHING_ADJUSTMENTS.denial_rate_multiplier})`,
    );

    // Teaching: more documentation time (residents)
    if (baselineMap.avg_doc_time_per_encounter_minutes) {
      baselineMap.avg_doc_time_per_encounter_minutes = {
        ...baselineMap.avg_doc_time_per_encounter_minutes,
        value:
          Math.round(
            baselineMap.avg_doc_time_per_encounter_minutes.value * 1.25 * 10,
          ) / 10,
        sourceLabel: `${baselineMap.avg_doc_time_per_encounter_minutes.sourceLabel} × 1.25 teaching adjustment`,
      };
      derivations.push(
        `Doc time adjusted for teaching status: ${baselineMap.avg_doc_time_per_encounter_minutes.value} min`,
      );
    }

    // Teaching: lower wRVUs (time spent teaching)
    if (baselineMap.avg_wrvus_per_physician_per_month) {
      baselineMap.avg_wrvus_per_physician_per_month = {
        ...baselineMap.avg_wrvus_per_physician_per_month,
        value: Math.round(
          baselineMap.avg_wrvus_per_physician_per_month.value * 0.85,
        ),
        sourceLabel: `${baselineMap.avg_wrvus_per_physician_per_month.sourceLabel} × 0.85 teaching adjustment`,
      };
      derivations.push(
        `wRVUs adjusted for teaching status: ${baselineMap.avg_wrvus_per_physician_per_month.value}`,
      );
    }
  }

  // Apply composite wage adjustment to all workforce roles
  if (Math.abs(wageMultiplier - 1.0) > 0.005) {
    for (const [_role, entry] of Object.entries(profile.workforce)) {
      if (entry && entry.avg_fully_loaded_cost > 0) {
        entry.avg_fully_loaded_cost = Math.round(
          entry.avg_fully_loaded_cost * wageMultiplier,
        );
      }
    }
    derivations.push(
      `Workforce costs adjusted ×${wageMultiplier.toFixed(3)} (${wageComponents.join(", ")})`,
    );
  }

  return { derivations };
}

/**
 * Convert baselineMap to BaselineRecord[].
 */
function toBaselineRecords(
  baselineMap: Record<string, EstimatedBaselineValue>,
): BaselineRecord[] {
  return Object.entries(baselineMap).map(
    ([key, { value, source, sourceLabel }]) => ({
      key,
      value,
      source,
      sourceLabel,
    }),
  );
}

/**
 * Weighted completeness scoring. Normalized 0-100.
 * Simplified: only depends on tier1 inputs.
 *
 * Weights:
 *   org_type: 8 (always provided)
 *   encounters: 22 user / 12 derived / 4 benchmark
 *   beds: 8
 *   payer_mix customized: 13
 *   specialties selected: 13
 *   physician_count: 8
 *   revenue: 8
 *   state: 8
 *   setting_type: 6
 *   teaching_status: 6
 */
function computeCompleteness(tier1: Tier1Inputs): number {
  let score = 0;
  const maxScore = 100;

  // org_type always provided (8 pts)
  score += 8;

  // Encounters (22 if user-provided, 12 if derived from beds/physicians, 4 if benchmark)
  if (tier1.total_encounters && tier1.total_encounters > 0) {
    score += 22;
  } else if (
    (tier1.licensed_beds && tier1.licensed_beds > 0) ||
    (tier1.physician_count && tier1.physician_count > 0)
  ) {
    score += 12;
  } else {
    score += 4; // benchmark fallback still gets some credit
  }

  // Beds (8 pts)
  if (tier1.licensed_beds && tier1.licensed_beds > 0) {
    score += 8;
  }

  // Payer mix customized (13 pts)
  if (tier1.payer_mix && Object.keys(tier1.payer_mix).length > 0) {
    const customizedKeys = Object.keys(tier1.payer_mix).length;
    score += Math.min(13, Math.round((customizedKeys / 6) * 13));
  }

  // Specialties selected (13 pts)
  if (tier1.selected_specialties && tier1.selected_specialties.length > 0) {
    score += 13;
  }

  // Physician count (8 pts)
  if (tier1.physician_count && tier1.physician_count > 0) {
    score += 8;
  }

  // Revenue (8 pts)
  if (tier1.net_patient_revenue && tier1.net_patient_revenue > 0) {
    score += 8;
  }

  // State (8 pts)
  if (tier1.state) {
    score += 8;
  }

  // Setting type (6 pts)
  if (tier1.setting_type) {
    score += 6;
  }

  // Teaching status gets credit if explicitly set (6 pts)
  if (tier1.teaching_status !== undefined) {
    score += 6;
  }

  return Math.min(maxScore, score);
}
